import { useCallback } from 'react';
import { useBrandSupplierRemoveMutation } from 'api';
import { useParams } from 'react-router-dom';
import { notification } from 'components';
import { ErrorMappingValueType } from 'typings';

export const useSupplierRemoveModal = ({
  onModalStateChange,
  supplierId,
}: {
  onModalStateChange: () => void;
  supplierId: string | null;
}) => {
  const { id } = useParams() as { id: string };
  const [removeSupplier, { isLoading }] = useBrandSupplierRemoveMutation();

  const handleCancel = useCallback(() => {
    onModalStateChange();
  }, [onModalStateChange]);

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({ message: message });
    },
    [notificationApi]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const payload = {
        brandId: id,
        supplierId,
      };
      const { data, error }: Record<string, any> = await removeSupplier(payload);
      if (data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'Supplier was successfully removed.',
        });
        handleCancel();
      } else if (error?.data?.success === false) {
        Object.values(error?.data?.data || {}).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else if (error?.data?.error) {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    } catch (err) {
      console.error('Error removing supplier:', err);
      openNotificationWithIcon({
        type: 'error',
        message: 'An unexpected error occurred. Please try again.',
      });
    }
  }, [id, openNotificationWithIcon, handleCancel, removeSupplier, supplierId]);

  return {
    handleCancel,
    handleSubmit,
    contextHolder,
    isLoading,
  };
};
